import ApiService from '../../../services/apiService';
import viewMixin from '../../../components/core/consumers/viewMixin';
import { mapState } from 'vuex';
import pullAt from 'lodash/pullAt';
import cloneDeep from 'lodash/cloneDeep';
export default {
  name: 'CoreTenantFederatedAuthClientConfig',
  mixins: [viewMixin],
  props: {
    tenant: {
      type: Object,
      required: true
    },
    clType: {
      type: Object,
      required: false,
      default: () => ({
        text: 'OAuth2',
        param: 'OAuth2'
      })
    }
  },
  data: function () {
    return {
      deleteDialog: false,
      deleteClientIndex: -1,
      clients: [],
      cMOptions: {
        tabSize: 4,
        mode: {
          name: 'javascript',
          json: true
        },
        firstLineNumber: 0,
        theme: this.$store.state.app.UiTheme ? 'base16-dark' : 'duotone-light',
        lineNumbers: true,
        line: true,
        foldGutter: true,
        styleActiveLine: true,
        styleSelectedText: true,
        hintOptions: {
          completeSingle: false
        },
        lineWrapping: false
      },
      clientType: this.clType
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'UiTheme']),
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    clientTypes() {
      return [{
        text: this.$i18n_t('accMgmt.tenant.federatedAuth.clientType.OAuth2'),
        param: 'OAuth2'
      }, {
        text: this.$i18n_t('accMgmt.tenant.federatedAuth.clientType.OIDC'),
        param: 'OIDC'
      }];
    }
  },
  watch: {
    tenant() {
      this.loadData();
    }
  },
  mounted() {
    this.loadData();
  },
  methods: {
    loadData() {
      const tenantId = this.tenant.id;

      // Fetch both OAuth2 and OIDC clients concurrently
      const oauthClientsPromise = ApiService.getOauthClientsForTenant(tenantId);
      const oidcClientsPromise = ApiService.getOIDCClientsForTenant(tenantId);
      return Promise.all([oauthClientsPromise, oidcClientsPromise]).then(([oauthClients, oidcClients]) => {
        const oauthClientsFormatted = (oauthClients || []).map(client => {
          client.json = JSON.stringify(client.properties, null, 4);
          client.clientColor = 'none';
          client.authType = 'OAuth2';
          client.collapsedConfig = 1;
          return client;
        });
        const oidcClientsFormatted = (oidcClients || []).map(client => {
          client.json = JSON.stringify(client.properties, null, 4);
          client.clientColor = 'none';
          client.authType = 'OIDC';
          client.collapsedConfig = 1;
          return client;
        });
        this.clients = [...oauthClientsFormatted, ...oidcClientsFormatted];
      });
    },
    addClient(clientType) {
      const apiHost = process.env.VUE_APP_API_HOST;
      const apiAuthPath = clientType === 'OAuth2' ? '/api/v1/oauth2/callback' : '/api/v1/oidc/callback';
      const redirectUri = apiHost ? `${apiHost}${apiAuthPath}` : `https://api.<<your_niota_domain>>${apiAuthPath}`;
      let emptyClient = {
        name: '',
        description: '',
        logo: '',
        json: JSON.stringify({
          client_id: '',
          client_secret: '',
          auth_uri: '',
          token_uri: '',
          user_info_uri: '',
          redirect_uri: redirectUri,
          scope: ''
        }, null, 4),
        clientColor: 'none',
        authType: clientType,
        collapsedConfig: 0
      };
      this.clients.unshift(emptyClient);
      this.clients = cloneDeep(this.clients);
    },
    async validateAndSave(index) {
      try {
        this.clients[index].properties = JSON.parse(this.clients[index].json);
      } catch (error) {
        this.setClientColor(index, 'failed');
        this.$snack(error.message, 'error');
        return;
      }
      const client = this.clients[index];
      const validateMethod = client.authType === 'OIDC' ? 'validateOIDCClient' : 'validateOauthClient';
      const createMethod = client.authType === 'OIDC' ? 'createOIDCClient' : 'createOauthClient';
      const updateMethod = client.authType === 'OIDC' ? 'updateOIDCClient' : 'updateOauthClient';
      try {
        await ApiService[validateMethod](client.properties);
      } catch (error) {
        this.setClientColor(index, 'failed');
        this.$snack(error.response.data.message, 'error');
        return;
      }
      let persistPromise;
      this.clients[index].clientColor = undefined;
      if (this.clients[index].clientId) {
        persistPromise = ApiService[updateMethod](this.clients[index].clientId, this.clients[index]);
      } else {
        persistPromise = ApiService[createMethod](this.tenant.id, this.clients[index]);
      }
      return persistPromise.then(clientObj => {
        clientObj.json = JSON.stringify(clientObj.properties, null, 4);
        this.clients[index] = clientObj;
        this.setClientColor(index, 'saved');
        this.clients = cloneDeep(this.clients);
        this.$snack('Validated and saved');
      }).catch(error => {
        this.clients[index].clientColor = 'red';
        if (error.response && this.$i18n_t(`Error.${error.response.data.message}`)) {
          this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
        } else {
          this.$snack(`${error.response.data.message}`, 'error');
        }
      });
    },
    deleteClient(index) {
      const client = this.clients[index];
      const deleteMethod = client.authType === 'OIDC' ? 'deleteOIDCClient' : 'deleteOauthClient';
      return ApiService[deleteMethod](client.clientId).then(() => {
        pullAt(this.clients, [index]);
        this.clients = cloneDeep(this.clients);
      });
    },
    changeLogo(index) {
      const file = this.$refs.attachmentInput[index].files[0];
      if (file.size > 1024 * 1024) {
        this.$snack('Image size should not exceed 512 kb', 'error');
        this.$refs.attachmentInput[index].files[0].value = null;
        return;
      }
      const reader = new FileReader();
      reader.onload = f => {
        this.clients[index].logo = f.target.result;
      };
      reader.readAsDataURL(file);
      this.setClientColor(index, 'changed');
    },
    removeLogo(index) {
      this.clients[index].logo = null;
      this.setClientColor(index, 'changed');
    },
    setClientColor(index, type) {
      switch (type) {
        case 'changed':
          this.clients[index].clientColor = 'orange';
          break;
        case 'saved':
          this.clients[index].clientColor = 'green';
          break;
        case 'failed':
          this.clients[index].clientColor = 'red';
          break;
        default:
          this.clients[index].clientColor = 'none';
      }
      if (type === 'saved') {
        setTimeout(() => {
          this.clients[index].clientColor = 'none';
        }, 2000);
      }
    }
  }
};